/**
 * SurveyForm — public renderer used by /s/:token public survey pages and
 * potentially by internal review pages. Handles all question types from
 * the Phase 3F builder and evaluates visibility branching client-side.
 *
 * Answer shape (matches backend expectations):
 *   yes_no / yes_no_na / single_choice / select / likert / rating / nps  → string | number
 *   short_text / long_text / date / email / phone                        → string
 *   number / currency / slider                                           → string (stringified number)
 *   multi_choice / ranking                                               → string[] (serialised as JSON string in the flat map)
 *   matrix                                                               → object  (serialised as JSON string)
 *   address                                                              → object  (serialised as JSON string)
 *
 * The flat `answers` map only ever stores strings for storage/API compat.
 * For array/object types the value is JSON-encoded on set and JSON-decoded
 * on read within this component.
 */
import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Info, MapPin } from 'lucide-react';
import { GeoAddressField } from '@/components/GeoAddressField';
import { DocumentUpload } from '@/components/DocumentUpload';
import { PhotoQuestion } from '@/components/PhotoQuestion';
import { useRegional } from '../lib/regional';
import { CameraCapture } from './CameraCapture';
import { VideoQuestion } from './VideoQuestion';

// ---------- Types ----------
interface Condition { question_code: string; op: string; value?: unknown }
export interface Question {
  question_code: string; display_number: string; text: string;
  type: string; required?: boolean; help_text?: string;
  options?: string[];
  condition?: Condition;
  conditions?: Condition[];
  visibility_logic?: 'all' | 'any';
  config?: Record<string, unknown>;
}
export interface Section { section_code: string; number: string; title: string; description?: string; questions: Question[] }
export interface SchemaShape { sections: Section[]; signature_slots?: Array<{ role: string; label: string }>; photo_gallery_enabled?: boolean; geofence_required?: boolean }

interface Props {
  schema: SchemaShape;
  answers: Record<string, string>;
  onChange: (code: string, value: string) => void;
  errors?: Record<string, string>;
  instructionId?: string;
  token?: string;
  /** When set, render ONLY this section (0-based) — used by the wizard flow. */
  activeSection?: number;
  /** Question codes auto-filled from GPS — rendered read-only. */
  lockedCodes?: string[];
}

// ---------- Branching engine ----------
const asComparable = (v: unknown): string | number => {
  if (typeof v === 'number') return v;
  const s = String(v ?? '').trim();
  const n = Number(s);
  return !Number.isNaN(n) && s !== '' ? n : s.toLowerCase();
};

const evalOne = (cond: Condition, answers: Record<string, string>): boolean => {
  const raw = answers[cond.question_code];
  if (cond.op === 'answered') return raw !== undefined && raw !== null && raw !== '';
  if (cond.op === 'unanswered') return raw === undefined || raw === null || raw === '';
  if (raw === undefined || raw === null || raw === '') return false;
  const actual = asComparable(raw);
  const expected = asComparable(cond.value);
  switch (cond.op) {
    case 'eq': return String(actual) === String(expected);
    case 'ne': return String(actual) !== String(expected);
    case 'gt': return typeof actual === 'number' && typeof expected === 'number' && actual > expected;
    case 'gte': return typeof actual === 'number' && typeof expected === 'number' && actual >= expected;
    case 'lt': return typeof actual === 'number' && typeof expected === 'number' && actual < expected;
    case 'lte': return typeof actual === 'number' && typeof expected === 'number' && actual <= expected;
    case 'contains': return String(actual).includes(String(expected));
    case 'in': {
      const list = String(cond.value ?? '').split(',').map((s) => s.trim().toLowerCase());
      return list.includes(String(actual));
    }
    case 'not_in': {
      const list = String(cond.value ?? '').split(',').map((s) => s.trim().toLowerCase());
      return !list.includes(String(actual));
    }
    default: return true;
  }
};

type VTNode =
  | { kind: 'rule'; question_code: string; op: string; value?: unknown }
  | { kind: 'group'; logic: 'all' | 'any'; children: VTNode[] };

const evalNode = (node: VTNode, answers: Record<string, string>): boolean => {
  if (node.kind === 'rule') return evalOne(node as unknown as Condition, answers);
  const results = (node.children || []).map((c) => evalNode(c, answers));
  if (results.length === 0) return true;
  return node.logic === 'any' ? results.some(Boolean) : results.every(Boolean);
};

export const isVisible = (q: Question, answers: Record<string, string>): boolean => {
  // Nested condition groups take precedence when present.
  const tree = (q as unknown as { visibility_tree?: VTNode }).visibility_tree;
  if (tree && tree.kind === 'group') return evalNode(tree, answers);
  // Legacy single condition
  if (q.condition && !evalOne(q.condition, answers)) return false;
  const conds = q.conditions ?? [];
  if (conds.length === 0) return true;
  const logic = q.visibility_logic || 'all';
  const results = conds.map((c) => evalOne(c, answers));
  return logic === 'any' ? results.some(Boolean) : results.every(Boolean);
};


// ---------- Helpers for array/object payloads ----------
const parseJsonSafe = <T,>(raw: string | undefined, fallback: T): T => {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
};

// ---------- Component ----------
export const SurveyForm: React.FC<Props> = ({ schema, answers, onChange, errors = {}, instructionId, token, activeSection, lockedCodes }) => {
  const visibleQuestions = useMemo(() => {
    const map: Record<string, boolean> = {};
    schema.sections.forEach((s) => s.questions.forEach((q) => { map[q.question_code] = isVisible(q, answers); }));
    return map;
  }, [schema, answers]);

  const sections = activeSection === undefined
    ? schema.sections
    : schema.sections.filter((_, i) => i === activeSection);

  return (
    <div className="space-y-6" data-testid="survey-form">
      {sections.map((section) => (
        <Card key={section.section_code}>
          <CardContent className="p-4">
            <div className="pb-3 mb-3 border-b border-border/60">
              <div className="font-headline text-raptor-navy text-lg tracking-wide">{section.number}. {section.title}</div>
              {section.description && <div className="text-xs text-raptor-navy/60 font-body mt-1">{section.description}</div>}
            </div>
            <div className="space-y-3">
              {section.questions.map((q) => {
                if (!visibleQuestions[q.question_code]) return null;
                const err = errors[q.question_code];
                const val = answers[q.question_code] ?? '';
                const testId = `q-${q.question_code}`;
                const cfg = (q.config || {}) as Record<string, unknown>;

                // Info block — no answer, just a callout
                if (q.type === 'info_block' || q.type === 'section_header') {
                  const body = (cfg.body as string) || q.help_text || q.text;
                  return (
                    <div key={q.question_code} className="rounded-md bg-raptor-blue/5 border border-raptor-blue/30 p-3 flex items-start gap-2" data-testid={testId}>
                      <Info size={16} className="text-raptor-blue mt-0.5 shrink-0" />
                      <div className="text-sm text-raptor-navy font-body whitespace-pre-wrap">{body}</div>
                    </div>
                  );
                }

                return (
                  <div key={q.question_code} className="grid grid-cols-1 md:grid-cols-[80px_1fr_320px] gap-2 md:gap-3 items-start py-2" data-testid={`${testId}-row`}>
                    <div className="text-raptor-blue font-tabular font-bold text-sm pt-1">{q.display_number}</div>
                    <div>
                      <Label className="leading-snug">
                        {q.text}{q.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {q.help_text && <div className="text-xs text-raptor-navy/50 font-body mt-1">{q.help_text}</div>}
                      {err && <div className="text-xs text-red-600 font-body mt-1">{err}</div>}
                    </div>
                    <div>
                      <QuestionInput q={q} value={val} onChange={(v) => onChange(q.question_code, v)} testId={testId} cfg={cfg} instructionId={instructionId} token={token} readOnly={lockedCodes?.includes(q.question_code)} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};


// -------------------------------------------------------------- Per-type input
const QuestionInput: React.FC<{
  q: Question; value: string; onChange: (v: string) => void; testId: string; cfg: Record<string, unknown>;
  instructionId?: string; token?: string; readOnly?: boolean;
}> = ({ q, value, onChange, testId, cfg, instructionId, token, readOnly }) => {
  const { regional } = useRegional();

  // Auto-filled location fields (GPS captured at survey start) are read-only.
  if (readOnly) {
    const display = (q.type === 'address' || q.type === 'address_only' || q.type === 'geo_only')
      ? (() => { const o = parseJsonSafe<{ lat?: number; lng?: number; formatted?: string }>(value, {}); return o.formatted || (o.lat != null ? `${o.lat}, ${o.lng}` : ''); })()
      : value;
    return (
      <div className="flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-raptor-neutral/30 text-sm font-tabular text-raptor-navy/80" data-testid={testId}>
        <MapPin size={14} className="text-raptor-blue shrink-0" />
        <span className="truncate">{display || '—'}</span>
        <span className="ml-auto text-[10px] uppercase tracking-wide text-raptor-navy/40">Auto</span>
      </div>
    );
  }

  // ---- Classic
  if (q.type === 'yes_no') {
    return (
      <div className="flex gap-2">
        {['Yes', 'No'].map((opt) => (
          <button key={opt} type="button" data-testid={`${testId}-${opt.toLowerCase()}`} onClick={() => onChange(opt.toLowerCase())}
            className={`flex-1 h-9 rounded-md text-sm font-semibold transition-all border ${value === opt.toLowerCase() ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-raptor-navy border-border hover:bg-raptor-neutral/50'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'yes_no_na') {
    return (
      <div className="flex gap-2">
        {['Yes', 'No', 'N/A'].map((opt) => (
          <button key={opt} type="button" onClick={() => onChange(opt.toLowerCase().replace('/', ''))}
            className={`flex-1 h-9 rounded-md text-xs font-semibold transition-all border ${value === opt.toLowerCase().replace('/', '') ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-raptor-navy border-border hover:bg-raptor-neutral/50'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'select') {
    return (
      <Select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {(q.options ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
      </Select>
    );
  }
  if (q.type === 'short_text' || q.type === 'email' || q.type === 'phone') {
    const type = q.type === 'email' ? 'email' : q.type === 'phone' ? 'tel' : 'text';
    const isText = q.type === 'short_text';
    const max = isText ? ((cfg.max_length as number) ?? 255) : (cfg.max_length as number) || undefined;
    return (
      <div className="grid gap-1">
        <Input type={type} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}
          placeholder={(cfg.placeholder as string) || ''} maxLength={max} spellCheck={isText} />
        {isText && max ? <div className="text-[11px] text-raptor-navy/50 text-right">{value.length}/{max}</div> : null}
      </div>
    );
  }
  if (q.type === 'long_text') {
    const max = (cfg.max_length as number) ?? 1500;
    return (
      <div className="grid gap-1">
        <Textarea data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} rows={3}
          placeholder={(cfg.placeholder as string) || ''} maxLength={max} spellCheck />
        <div className="text-[11px] text-raptor-navy/50 text-right">{value.length}/{max}</div>
      </div>
    );
  }
  if (q.type === 'date') {
    return <Input type="date" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (q.type === 'number' || q.type === 'currency') {
    // Currency symbol defaults to the tenant's global regional setting
    // (falls back to a per-question override, then 'R').
    const symbol = q.type === 'currency'
      ? ((cfg.currency as string) || regional.currency_symbol || 'R')
      : '';
    const after = q.type === 'currency' && regional.currency_symbol_position === 'after';
    return (
      <div className="flex items-center gap-2">
        {symbol && !after && <span className="text-sm text-raptor-navy/70 font-tabular">{symbol}</span>}
        <Input type="number" data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="font-tabular"
          min={cfg.min as number | undefined} max={cfg.max as number | undefined} step={q.type === 'currency' ? '0.01' : '1'} />
        {symbol && after && <span className="text-sm text-raptor-navy/70 font-tabular">{symbol}</span>}
      </div>
    );
  }

  // ---- Choice-based (new)
  if (q.type === 'single_choice') {
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options ?? []).map((opt) => (
          <button key={opt} type="button" data-testid={`${testId}-${opt}`} onClick={() => onChange(opt)}
            className={`px-3 h-9 rounded-md text-sm border transition-all ${value === opt ? 'bg-raptor-blue text-white border-raptor-blue' : 'bg-white text-raptor-navy border-border hover:bg-raptor-neutral/50'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'multi_choice') {
    const arr = parseJsonSafe<string[]>(value, []);
    const toggle = (opt: string): void => {
      const next = arr.includes(opt) ? arr.filter((v) => v !== opt) : [...arr, opt];
      onChange(JSON.stringify(next));
    };
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {(q.options ?? []).map((opt) => {
          const checked = arr.includes(opt);
          return (
            <label key={opt} data-testid={`${testId}-${opt}`} className={`flex items-center gap-2 px-3 h-9 rounded-md border text-sm cursor-pointer transition-all ${checked ? 'bg-raptor-blue/10 border-raptor-blue text-raptor-navy' : 'bg-white border-border hover:bg-raptor-neutral/40'}`}>
              <input type="checkbox" checked={checked} onChange={() => toggle(opt)} />
              <span>{opt}</span>
            </label>
          );
        })}
      </div>
    );
  }

  // ---- Scales / ratings (new)
  if (q.type === 'nps') {
    const min = (cfg.scale_min as number) ?? 0;
    const max = (cfg.scale_max as number) ?? 10;
    const cur = Number(value);
    const scale = Array.from({ length: max - min + 1 }, (_, i) => i + min);
    const color = (n: number): string => n <= 6 ? 'bg-red-500 text-white' : n <= 8 ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white';
    return (
      <div className="flex flex-wrap gap-1" data-testid={testId}>
        {scale.map((n) => (
          <button key={n} type="button" data-testid={`${testId}-${n}`} onClick={() => onChange(String(n))}
            className={`w-9 h-9 rounded-md text-sm font-tabular border transition-all ${cur === n ? color(n) : 'bg-white text-raptor-navy border-border hover:bg-raptor-neutral/50'}`}>
            {n}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'rating') {
    const max = (cfg.scale_max as number) ?? 5;
    const cur = Number(value);
    return (
      <div className="flex gap-1" data-testid={testId}>
        {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" data-testid={`${testId}-${n}`} onClick={() => onChange(String(n))}
            className={`w-9 h-9 rounded-md text-lg font-tabular border transition-all ${cur >= n ? 'bg-amber-400 text-white border-amber-400' : 'bg-white text-raptor-navy border-border hover:bg-raptor-neutral/40'}`}>
            ★
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'likert') {
    return (
      <div className="flex flex-wrap gap-1">
        {(q.options ?? ['Strongly disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly agree']).map((opt) => (
          <button key={opt} type="button" data-testid={`${testId}-${opt}`} onClick={() => onChange(opt)}
            className={`px-2 h-8 rounded-md text-xs border transition-all ${value === opt ? 'bg-raptor-blue text-white border-raptor-blue' : 'bg-white text-raptor-navy border-border hover:bg-raptor-neutral/50'}`}>
            {opt}
          </button>
        ))}
      </div>
    );
  }
  if (q.type === 'slider') {
    const min = (cfg.scale_min as number) ?? 0;
    const max = (cfg.scale_max as number) ?? 100;
    const step = (cfg.scale_step as number) ?? 1;
    const cur = value === '' ? Math.round((min + max) / 2) : Number(value);
    return (
      <div className="flex items-center gap-3">
        <input type="range" min={min} max={max} step={step} value={cur} onChange={(e) => onChange(e.target.value)}
          data-testid={testId} className="flex-1 accent-raptor-blue" />
        <span className="font-tabular text-sm w-10 text-right">{cur}</span>
      </div>
    );
  }

  // ---- Ranking
  if (q.type === 'ranking') {
    const arr = parseJsonSafe<string[]>(value, [...(q.options ?? [])]);
    const move = (i: number, delta: number): void => {
      const next = [...arr];
      const tgt = i + delta;
      if (tgt < 0 || tgt >= next.length) return;
      [next[i], next[tgt]] = [next[tgt], next[i]];
      onChange(JSON.stringify(next));
    };
    return (
      <div className="space-y-1" data-testid={testId}>
        {arr.map((opt, i) => (
          <div key={opt} className="flex items-center gap-2 rounded-md border border-border/60 bg-white px-2 h-9 text-sm">
            <span className="w-6 h-6 rounded-full bg-raptor-blue/10 text-raptor-blue text-xs font-bold flex items-center justify-center">{i + 1}</span>
            <span className="flex-1 truncate">{opt}</span>
            <button type="button" onClick={() => move(i, -1)} className="p-1 hover:bg-raptor-neutral/40 rounded" aria-label="Move up">↑</button>
            <button type="button" onClick={() => move(i, +1)} className="p-1 hover:bg-raptor-neutral/40 rounded" aria-label="Move down">↓</button>
          </div>
        ))}
      </div>
    );
  }

  // ---- Matrix
  if (q.type === 'matrix') {
    const rows: string[] = (cfg.rows as string[]) || [];
    const cols: string[] = (cfg.columns as string[]) || [];
    const grid = parseJsonSafe<Record<string, string>>(value, {});
    const setCell = (row: string, col: string): void => {
      onChange(JSON.stringify({ ...grid, [row]: col }));
    };
    return (
      <div className="overflow-x-auto rounded-md border border-border/60 bg-white" data-testid={testId}>
        <table className="w-full text-xs font-body">
          <thead className="bg-raptor-neutral/40">
            <tr>
              <th className="p-2 text-left"></th>
              {cols.map((c) => <th key={c} className="p-2 text-center">{c}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r} className="border-t border-border/60">
                <td className="p-2 font-semibold text-raptor-navy">{r}</td>
                {cols.map((c) => (
                  <td key={c} className="p-1 text-center">
                    <input
                      type="radio"
                      name={`${testId}-${r}`}
                      checked={grid[r] === c}
                      onChange={() => setCell(r, c)}
                      data-testid={`${testId}-${r}-${c}`}
                      className="accent-raptor-blue"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // ---- Address: GPS-aware picker (reverse-geocode closest match or manual)
  if (q.type === 'address' || q.type === 'address_only' || q.type === 'geo_only') {
    const mode = q.type === 'geo_only' ? 'geo_only' : q.type === 'address_only' ? 'address_only' : ((cfg.location_mode as string) || 'address_geo');
    return <GeoAddressField value={value} onChange={onChange} testId={testId} mode={mode} />;
  }

  // ---- Photo (per-question camera capture — single or multiple, no uploads) ----
  if (q.type === 'photo_ref') {
    if (instructionId) {
      return (
        <PhotoQuestion instructionId={instructionId} token={token} questionCode={q.question_code}
          caption={q.text} value={value} onChange={onChange} testId={testId}
          multiple={cfg.multiple !== false} maxCount={(cfg.max_count as number) ?? 5} maxMb={(cfg.max_mb as number) ?? 2}
          facing={(cfg.camera_facing as 'any' | 'front' | 'back') ?? 'any'} allowSwitch={cfg.allow_switch !== false} />
      );
    }
    return (
      <div className="text-xs text-raptor-navy/60 italic font-body">
        Captured via the photo gallery below.
      </div>
    );
  }

  // ---- Video (record-live — no uploads) ----
  if (q.type === 'video_ref') {
    if (instructionId) {
      return (
        <VideoQuestion instructionId={instructionId} token={token} questionCode={q.question_code}
          caption={q.text} value={value} onChange={onChange} testId={testId}
          maxMb={(cfg.max_mb as number) ?? 50} maxDurationSec={(cfg.max_duration_sec as number) ?? 30}
          facing={(cfg.camera_facing as 'any' | 'front' | 'back') ?? 'any'} allowSwitch={cfg.allow_switch !== false} />
      );
    }
    return (
      <div className="text-xs text-raptor-navy/60 italic font-body">
        Recorded on a mobile device.
      </div>
    );
  }

  // ---- Document upload (true file upload — PDF / JPG / PNG) ----
  if (q.type === 'document_ref') {
    return (
      <DocumentUpload instructionId={instructionId} token={token} questionCode={q.question_code}
        value={value} onChange={onChange} testId={testId}
        maxCount={(cfg.max_count as number) ?? 10} maxMb={(cfg.max_mb as number) ?? 2} />
    );
  }

  // ---- Signature (signature pad below) ----
  if (q.type === 'signature_ref') {
    return (
      <div className="text-xs text-raptor-navy/60 italic font-body">
        Captured via the signature pad below.
      </div>
    );
  }

  // ---- Fallback
  return <Input data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} />;
};

export default SurveyForm;
